import { useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { toastService } from "@miplatform/toast";
import { IAttemptListItemResponseForWorkOrder } from "@miplatform/types";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import AssignableUserColumn from "../UnassignedWorkOrders/AssignableUserColumn";
import { AssignableUserContainer } from "../UnassignedWorkOrders/AssignableUserContainer";
import { UnassignedWorkOrders } from "../UnassignedWorkOrders/UnassignedWorkOrders";
import { AssignmentViewActions } from "./AssignmentViewActions";
import { CancelAssignmentStagingModal } from "./CancelAssignmentStagingModal";
import { CloseAssignableUserColumnModal } from "./CloseAssignableUserColumnModal";
import { useAssignmentStaging } from "./useAssignmentStaging";
import { useNHCPriorityScheduler } from "./useNHCPriorityScheduler";

const COLUMN_WIDTH = 180;
const HOUR_HEIGHT = 60;

export function AssignmentView() {
  const { classes } = useStyles();
  const { zoom, date, calendarSet, events } = useCalendarContext();
  const [discardStagingModalOpen, setDiscardStagingModalOpen] = useState(false);
  const [closingUserId, setClosingUserId] = useState<string | null>(null);

  const {
    activeAssignableUserIds,
    assignableUserSummaries,
    boardExpandedUserId,
    effectiveSelectedUserId,
    expandedUsers,
    handleAddAssignableUser,
    handleBoardExpandedChange,
    handleCloseUserColumn,
    handleUserSelect,
  } = useNHCPriorityScheduler();

  const {
    stagedByUserId,
    stagedIds,
    confirmedAttemptIds,
    totalStagedCount,
    isConfirming,
    stageWorkOrderForUser,
    undoLastTransfer,
    confirmStaging,
    clearStaging,
    clearStagingForUser,
    unstageAttempt,
    getStagedForUser,
  } = useAssignmentStaging(calendarSet?.config ?? null, date, events);

  const stagedCountByUserId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(stagedByUserId).map(([userId, entries]) => [
          userId,
          entries.length,
        ])
      ),
    [stagedByUserId]
  );

  const handleWorkOrderClick = (
    attempt: IAttemptListItemResponseForWorkOrder
  ) => {
    const targetUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];
    if (!targetUserId) {
      return;
    }
    if (!effectiveSelectedUserId && activeAssignableUserIds[0]) {
      handleUserSelect(activeAssignableUserIds[0]);
    }
    stageWorkOrderForUser(attempt, targetUserId);
  };

  const handleCloseAssignableColumn = (userId: string) => {
    clearStagingForUser(userId);
    handleCloseUserColumn(userId);
  };

  const handleRequestCloseAssignableColumn = (userId: string) => {
    const userStagedCount = stagedByUserId[userId]?.length ?? 0;
    if (userStagedCount > 0) {
      setClosingUserId(userId);
      return;
    }
    handleCloseAssignableColumn(userId);
  };

  const handleRequestDiscardStaging = () => {
    if (totalStagedCount > 0) {
      setDiscardStagingModalOpen(true);
    }
  };

  const handleCloseDiscardStagingModal = () => {
    setDiscardStagingModalOpen(false);
  };

  const handleConfirmDiscardStaging = () => {
    clearStaging();
    setDiscardStagingModalOpen(false);
  };

  const handleCloseUserConfirmationModal = () => {
    setClosingUserId(null);
  };

  const handleConfirmCloseAssignableColumn = () => {
    if (!closingUserId) {
      return;
    }
    handleCloseAssignableColumn(closingUserId);
    setClosingUserId(null);
  };

  const handleRemoveStagedAttempt = (userId: string, attemptId: string) => {
    unstageAttempt(userId, attemptId);
  };

  const handleConfirmStaging = async () => {
    const success = await confirmStaging();
    if (success) {
      toastService.success({
        text: "Work orders assigned successfully.",
      });
    }
  };

  const hasVisibleAssignableColumns = activeAssignableUserIds.length > 0;
  const actionsDisabled = totalStagedCount === 0;
  const closingUserStagedCount =
    closingUserId != null ? stagedByUserId[closingUserId]?.length ?? 0 : 0;

  return (
    <Box className={classes.container}>
      <Box className={classes.assignmentPanel}>
        <AssignableUserContainer
          users={assignableUserSummaries}
          onUserSelect={handleUserSelect}
          selectedUserId={effectiveSelectedUserId}
          activeAssignableUserIds={activeAssignableUserIds}
          stagedCountByUserId={stagedCountByUserId}
          onAddAssignableUser={handleAddAssignableUser}
        />
        <UnassignedWorkOrders
          stagedAttemptIds={stagedIds}
          confirmedAttemptIds={confirmedAttemptIds}
          onWorkOrderClick={handleWorkOrderClick}
        />
        {hasVisibleAssignableColumns && (
          <AssignmentViewActions
            stagedCount={totalStagedCount}
            disabled={actionsDisabled || isConfirming}
            onCancel={handleRequestDiscardStaging}
            onUndo={undoLastTransfer}
            onConfirm={handleConfirmStaging}
          />
        )}
        <CancelAssignmentStagingModal
          open={discardStagingModalOpen}
          stagedCount={totalStagedCount}
          onClose={handleCloseDiscardStagingModal}
          onConfirmCancel={handleConfirmDiscardStaging}
        />
        <CloseAssignableUserColumnModal
          open={closingUserId !== null}
          stagedCount={closingUserStagedCount}
          onClose={handleCloseUserConfirmationModal}
          onConfirmClose={handleConfirmCloseAssignableColumn}
        />
      </Box>

      <Box className={classes.drawersPanel}>
        {expandedUsers.map((user) => {
          const isBoardExpandedUser =
            boardExpandedUserId !== null && user.id === boardExpandedUserId;
          if (boardExpandedUserId !== null && user.id !== boardExpandedUserId) {
            return null;
          }
          return (
            <AssignableUserColumn
              key={user.id}
              user={user}
              selectedDate={date}
              selected={user.id === effectiveSelectedUserId}
              columnWidth={COLUMN_WIDTH}
              hourHeight={HOUR_HEIGHT}
              zoom={zoom}
              stagedAssignments={getStagedForUser(user.id)}
              assignedEvents={events.filter(
                (event) => event.assignedUserId === user.id
              )}
              onRemoveStagedAttempt={(attemptId) =>
                handleRemoveStagedAttempt(user.id, attemptId)
              }
              onClose={() => handleRequestCloseAssignableColumn(user.id)}
              boardExpanded={isBoardExpandedUser}
              onBoardExpandedChange={(expanded: boolean) =>
                handleBoardExpandedChange(user.id, expanded)
              }
            />
          );
        })}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    flex: 1,
    overflow: "auto",
  },
  assignmentPanel: {
    padding: theme.spacing(1),
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  drawersPanel: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
  },
}));
